import { useCallback } from 'react';

import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { useHasBizyNotification } from './use-has-bizy-notification';

/**
 * Click handler for the Ask Bizy entry point (navbar item, super-admin
 * AskBizyButton, anywhere else this entry point lives). Centralizes the
 * unread-aware engagement semantics:
 *
 *   - Has unread (panel CLOSED or OPEN) → openFromNotificationPeek.
 *     Opens the panel if needed, sets `lastTrigger` so the coordinator's
 *     peek branch picks it up on first open, and bumps
 *     `engageRequestVersion` so the coordinator engages even if the panel
 *     was already open. Either way the user lands on the proactive
 *     conversation immediately, instead of having to find it in the
 *     dropdown.
 *   - No unread → plain toggle.
 *
 * Lives in one place so the navbar entry and the super-admin button stay
 * in lockstep when the engagement semantics evolve.
 */
export function useBizyAskClick(): () => void {
	const toggle = useBizySidePanelStore((s) => s.toggle);
	const openFromNotificationPeek = useBizySidePanelStore((s) => s.openFromNotificationPeek);
	const hasUnread = useHasBizyNotification();

	return useCallback(() => {
		if (hasUnread) {
			openFromNotificationPeek();
			return;
		}
		toggle();
	}, [hasUnread, openFromNotificationPeek, toggle]);
}
